/**
 * Drift checker registry — declarative. Each checker declares what it needs
 * from the DriftContext; the runner synthesizes the call.
 *
 * Adding a checker = one entry in CHECKERS[] with `needs` and a single-call
 * `run`. See `.cai/patterns/add-checker.md`.
 */
import { relative } from "node:path";
import type {
  CheckerSummary,
  Claim,
  DriftCheckerName,
  DriftContext,
  DriftIssue,
} from "../types.js";
import { checkBrokenLinks } from "./checkers/broken-link.js";
import { checkCommands } from "./checkers/command.js";
import { checkCorrectionClusters } from "./checkers/correction-clusters.js";
import { checkCrossFile } from "./checkers/cross-file.js";
import { checkDependencies } from "./checkers/dependency.js";
import { checkEdgesAll } from "./checkers/edges.js";
import { checkEnvVars } from "./checkers/env-vars.js";
import { checkIndexSync } from "./checkers/index-sync.js";
import { checkLinks } from "./checkers/links.js";
import { checkPaths } from "./checkers/path.js";
import { checkRuleGlobs } from "./checkers/rule-globs.js";
import { checkScriptCoverage } from "./checkers/script-coverage.js";
import { checkStalenessBatch } from "./checkers/staleness.js";
import { checkTodoFixme } from "./checkers/todo-fixme.js";
import { checkToolConfigs, getExistingToolConfigs } from "./checkers/tool-configs.js";
import { checkVersionPins } from "./checkers/version-pins.js";
import { checkWorkspaceDependencies } from "./checkers/workspace-dependencies.js";
import { loadScaffoldText } from "./checkers/shared.js";

// ── Result type that every checker produces ─────────────────────────────────

export interface CheckerRunResult {
  issues: DriftIssue[];
  checked: number;
}

// ── Fields a checker can declare it needs ───────────────────────────────────

export type CheckerNeeds =
  | "claims"
  | "scaffoldFiles"
  | "projectRoot"
  | "subProjectRoot"
  | "scaffoldRoot"
  | "project"
  | "staleness"
  | "config";

/**
 * The narrowed context a checker actually sees: only the fields it asked
 * for, all required (no `?`, no `!`). The projection is enforced by the
 * type system via `bindCheckerDeclaration<K>`.
 *
 * Out-of-`needs` fields are typed as `never` via the `Exclude` mapped type,
 * so reading them from a checker's `run(ctx)` is a TypeScript compile error.
 * Fields declared in `needs` are projected with their real DriftContext type.
 * The default `K = readonly CheckerNeeds[]` keeps backward compatibility for
 * callers that don't pin `K` — every field is then allowed (the empty `Exclude`
 * means the `never`-mapped portion is empty, so the intersection is just the
 * full projection).
 */
export type CheckerCallContext<K extends readonly CheckerNeeds[] = readonly CheckerNeeds[]> = {
  [P in Exclude<keyof DriftContext, K[number]>]: never;
} & {
  [P in K[number]]: DriftContext[P];
};

// ── The declaration: what the registry needs to know ────────────────────────

export interface CheckerDeclaration<K extends readonly CheckerNeeds[] = readonly CheckerNeeds[]> {
  readonly name: DriftCheckerName;
  /** Fields of DriftContext the checker reads. Minimum set — no extras. */
  readonly needs: K;
  /** Compute the "checked" count for the summary. Defaults to issues.length. */
  readonly checked?: (ctx: CheckerCallContext<K>) => number;
  readonly run: (ctx: CheckerCallContext<K>) => DriftIssue[] | Promise<DriftIssue[]>;
}

/**
 * The high-level interface used by the runner. Construct one of these from
 * a CheckerDeclaration with `bindCheckerDeclaration()`.
 */
export interface DriftChecker {
  name: DriftCheckerName;
  run(fullContext: DriftContext): Promise<CheckerRunResult> | CheckerRunResult;
}

// ── Shared declaration-time helpers ─────────────────────────────────────────

/** Count claim-typed inputs that this checker consumes, excluding negated ones. */
export const nonNegatedClaimsByKind = (
  kinds: readonly Claim["kind"][]
): ((ctx: { claims?: Claim[] }) => number) => {
  return (ctx) => {
    const claims = ctx.claims ?? [];
    return claims.filter((c) => kinds.includes(c.kind) && !c.negated).length;
  };
};

/** Map absolute file paths to project-relative paths. */
const relativize = (files: readonly string[], root: string): string[] =>
  files.map((f) => relative(root, f));

// ── Bind a declaration into a runnable checker ──────────────────────────────

export function bindCheckerDeclaration<K extends readonly CheckerNeeds[]>(
  decl: CheckerDeclaration<K>
): DriftChecker {
  return {
    name: decl.name,
    async run(fullContext) {
      // Project exactly the fields declared in `needs`. Because `K` is the
      // declaration's narrow tuple, the projected object satisfies
      // CheckerCallContext<K> with every field required.
      const projected = {} as CheckerCallContext<K>;
      for (const k of decl.needs) {
        (projected as Record<string, unknown>)[k] = fullContext[k];
      }

      const issues = await decl.run(projected);
      const checked = decl.checked ? decl.checked(projected) : issues.length;
      return { issues, checked };
    },
  };
}

// ── The 17 checker declarations ─────────────────────────────────────────────

export const CHECKERS: readonly CheckerDeclaration<any>[] = [
  {
    name: "edges",
    needs: ["scaffoldFiles", "projectRoot", "scaffoldRoot"],
    checked: (ctx) => ctx.scaffoldFiles.length,
    run(ctx) {
      return checkEdgesAll(ctx.scaffoldFiles, ctx.projectRoot, ctx.scaffoldRoot);
    },
  },
  {
    name: "staleness",
    needs: ["scaffoldFiles", "projectRoot", "staleness"],
    checked: (ctx) => ctx.scaffoldFiles.length,
    async run(ctx) {
      const sources = relativize(ctx.scaffoldFiles, ctx.projectRoot);
      return await checkStalenessBatch(sources, ctx.projectRoot, ctx.staleness);
    },
  },
  {
    name: "path",
    needs: ["claims", "subProjectRoot", "scaffoldRoot"],
    checked: nonNegatedClaimsByKind(["path"]),
    run(ctx) {
      return checkPaths(ctx.claims, ctx.subProjectRoot, ctx.scaffoldRoot);
    },
  },
  {
    name: "command",
    needs: ["claims", "projectRoot", "subProjectRoot", "project"],
    checked: nonNegatedClaimsByKind(["command"]),
    run(ctx) {
      return checkCommands(
        ctx.claims!,
        ctx.projectRoot!,
        ctx.project!,
        ctx.subProjectRoot!
      );
    },
  },
  {
    name: "dependency",
    needs: ["claims", "projectRoot", "project"],
    checked: nonNegatedClaimsByKind(["dependency", "version"]),
    run(ctx) {
      return checkDependencies(ctx.claims, ctx.projectRoot, ctx.project);
    },
  },
  {
    name: "cross-file",
    needs: ["claims"],
    checked: nonNegatedClaimsByKind(["version"]),
    run(ctx) {
      return checkCrossFile(ctx.claims);
    },
  },
  {
    name: "workspace-dependencies",
    needs: ["scaffoldFiles", "project", "projectRoot"],
    checked: (ctx) => ctx.project.workspaceDependencies.length,
    run(ctx) {
      return checkWorkspaceDependencies(ctx.scaffoldFiles, ctx.project, ctx.projectRoot);
    },
  },
  {
    name: "index-sync",
    needs: ["projectRoot", "scaffoldRoot"],
    run(ctx) {
      return checkIndexSync(ctx.projectRoot, ctx.scaffoldRoot);
    },
  },
  {
    name: "tool-configs",
    needs: ["projectRoot"],
    checked: (ctx) => getExistingToolConfigs(ctx.projectRoot).length,
    run(ctx) {
      return checkToolConfigs(ctx.projectRoot);
    },
  },
  {
    name: "script-coverage",
    needs: ["scaffoldFiles", "project"],
    run(ctx) {
      return checkScriptCoverage(ctx.scaffoldFiles, ctx.project);
    },
  },
  {
    name: "env-vars",
    needs: ["scaffoldFiles", "projectRoot"],
    run(ctx) {
      return checkEnvVars(ctx.scaffoldFiles, ctx.projectRoot, loadScaffoldText(ctx.scaffoldFiles));
    },
  },
  {
    name: "correction-clusters",
    needs: ["projectRoot", "scaffoldRoot"],
    run(ctx) {
      return checkCorrectionClusters(ctx.projectRoot, ctx.scaffoldRoot);
    },
  },
  {
    name: "version-pins",
    needs: ["subProjectRoot"],
    run(ctx) {
      return checkVersionPins(ctx.subProjectRoot);
    },
  },
  {
    name: "broken-link",
    needs: ["scaffoldFiles", "projectRoot", "scaffoldRoot"],
    checked: (ctx) => ctx.scaffoldFiles.length,
    run(ctx) {
      return checkBrokenLinks(ctx.scaffoldFiles, ctx.projectRoot, ctx.scaffoldRoot);
    },
  },
  {
    name: "todo-fixme",
    needs: ["scaffoldFiles", "projectRoot"],
    checked: (ctx) => ctx.scaffoldFiles.length,
    run(ctx) {
      return checkTodoFixme(ctx.scaffoldFiles, ctx.projectRoot);
    },
  },
  {
    name: "links",
    needs: ["scaffoldFiles", "projectRoot"],
    checked: (ctx) => ctx.scaffoldFiles.length,
    run(ctx) {
      return checkLinks(ctx.scaffoldFiles, ctx.projectRoot);
    },
  },
  {
    name: "rule-globs",
    needs: ["projectRoot"],
    run(ctx) {
      return checkRuleGlobs(ctx.projectRoot);
    },
  },
];

// ── Public API ──────────────────────────────────────────────────────────────

export const AVAILABLE_DRIFT_CHECKERS: DriftCheckerName[] = CHECKERS.map((c) => c.name);

/** Checkers that are NOT run by default and only run when explicitly requested
 *  via `--only`. `rule-globs` is opt-in because generated rules legitimately
 *  carry forward-looking globs (e.g. `docker-compose*`, `tsconfig.json`) that
 *  match no files yet — flagging them on a default run would be noise. */
export const OPT_IN_DRIFT_CHECKERS: DriftCheckerName[] = ["rule-globs"];

export function createDriftRegistry(): DriftChecker[] {
  return CHECKERS.map(bindCheckerDeclaration);
}

export function selectDriftRegistry(
  registry: DriftChecker[],
  opts: { only?: DriftCheckerName[]; skip?: DriftCheckerName[] }
): DriftChecker[] {
  const only = opts.only ? new Set(opts.only) : null;
  const skip = opts.skip ? new Set(opts.skip) : null;
  const optIn = new Set(OPT_IN_DRIFT_CHECKERS);

  return registry.filter((checker) => {
    if (only) return only.has(checker.name);
    if (optIn.has(checker.name)) return false;
    if (skip && skip.has(checker.name)) return false;
    return true;
  });
}

export async function runCheckerRegistry(
  context: DriftContext,
  registry = createDriftRegistry()
): Promise<{ issues: DriftIssue[]; summaries: CheckerSummary[] }> {
  const allIssues: DriftIssue[] = [];
  const summaries: CheckerSummary[] = [];

  for (const checker of registry) {
    try {
      const result = await checker.run(context);
      allIssues.push(...result.issues);
      summaries.push({
        name: checker.name,
        checked: result.checked,
        issuesFound: result.issues.length,
      });
    } catch (err) {
      const message = (err as Error).message;
      allIssues.push({
        code: "CHECKER_ERROR",
        severity: "warning",
        file: `[checker:${checker.name}]`,
        line: null,
        message: `Checker '${checker.name}' failed: ${message}`,
      });
      summaries.push({ name: checker.name, checked: 0, issuesFound: 0, error: message });
    }
  }

  return { issues: allIssues, summaries };
}